import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';

import type { TicketDeviceRequest } from '@cobliteam/common-types';

import {
  type CrmReportProblemTicket,
  type CrmReportProblemTicketsDto,
  CrmRequester,
  type CrmTicketDto,
  type CrmTicketsDto,
} from '@cobliteam/api-lib';

import { Alert } from '@cobliteam/alert';
import { Environment } from '@cobliteam/react-environments';

import useUser from '../useUser';

export const CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY =
  'CRM_TICKETS_SCHEDULE_INSTALLATION';
export const CRM_DEVOLUTION_QUERY_KEY = 'CRM_TICKETS_SCHEDULE_INSTALLATION';
const env = Environment.getInstance().getEnvironment();
const crmRequester = new CrmRequester({ host: env.apiHost });

const useCrm = () => {
  const { getSession } = useUser();
  const fleetId = getSession().fleet.id;

  const queryClient = useQueryClient();

  const getScheduleInstallationTicket = () => {
    return useQuery<CrmTicketDto | null>({
      queryKey: [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, fleetId],
      queryFn: () => crmRequester.getTickets(),
    });
  };

  const getDevolutionTickets = () => {
    return useQuery<CrmTicketsDto>({
      queryKey: [CRM_DEVOLUTION_QUERY_KEY],
      queryFn: () => crmRequester.getDevolutionTickets(),
    });
  };

  const notResponsibleForInstallation = () => {
    const { data: ticket } = getScheduleInstallationTicket();
    const ticketId = ticket?.id;

    return useMutation<string, undefined>({
      mutationFn: async () => crmRequester.notResponsible(ticketId),
    });
  };

  const responsibleForInstallation = () => {
    const { data: ticket } = getScheduleInstallationTicket();
    const ticketId = ticket?.id;

    return useMutation<string, undefined>({
      mutationFn: async () => crmRequester.responsible(ticketId),
      onSuccess: () => {
        queryClient.resetQueries([
          CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
          fleetId,
        ]);
      },
    });
  };

  const confirmInstallation = () => {
    const { data: ticket } = getScheduleInstallationTicket();
    const ticketId = ticket?.id;

    return useMutation<string, undefined>({
      mutationFn: async () => crmRequester.confirmInstallation(ticketId),
      onError: () => {
        Alert.error('Erro ao confirmar a instalação.');
      },
      onSuccess: () => {
        queryClient.resetQueries([
          CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
          fleetId,
        ]);
      },
    });
  };

  const cancelInstallation = () => {
    const { data: ticket } = getScheduleInstallationTicket();
    const ticketId = ticket?.id;

    return useMutation<undefined, undefined>({
      mutationFn: async () => crmRequester.cancelInstallation(ticketId),
      onError: () => {
        Alert.error('Erro ao cancelar a instalação.');
      },
    });
  };

  const clientAlreadyHasATechnician = () => {
    const { data: ticket } = getScheduleInstallationTicket();
    const ticketId = ticket?.id;

    return useMutation({
      mutationFn: () => crmRequester.clientAlreadyHasATechnician(ticketId),
      onError: () => {
        Alert.error('Erro ao atualizar o agendamento de instalação.');
      },
      onSuccess: () =>
        queryClient.refetchQueries([
          CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
          fleetId,
        ]),
    });
  };

  const rateInstallation = () => {
    const { data: ticket } = getScheduleInstallationTicket();
    const ticketId = ticket?.id;

    return useMutation({
      mutationFn: () => crmRequester.rateInstallation(ticketId),
      onError: () => {
        Alert.error('Erro ao avaliar a instalação.');
      },
      onSuccess: () =>
        queryClient.refetchQueries([
          CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
          fleetId,
        ]),
    });
  };

  const useGetTicket = (deviceId: string, options?: UseQueryOptions<any>) => {
    return useQuery<CrmReportProblemTicketsDto>({
      queryKey: [
        CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
        deviceId,
        'TICKETS',
      ],
      queryFn: () => crmRequester.searchTicket(deviceId),
      ...options,
    });
  };

  const useCreateTicket = (deviceId: string) => {
    return useMutation({
      mutationFn: (ticketRequest: TicketDeviceRequest) => {
        return crmRequester.reportProblem(deviceId, ticketRequest);
      },
      onMutate: async (newTicket) => {
        const KEY_QUERY = [
          CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
          deviceId,
          'TICKETS',
        ];

        await queryClient.cancelQueries({ queryKey: KEY_QUERY });
        const previousTickets: CrmReportProblemTicketsDto | undefined =
          queryClient.getQueryData(KEY_QUERY);

        queryClient.setQueryData<CrmReportProblemTicketsDto | undefined>(
          KEY_QUERY,
          (data: any) => {
            if (!data) return data;

            const tempTicket: CrmReportProblemTicket = {
              id: 'Aguardando Nº do ticket...',
              createdAt: new Date().toISOString(),
              properties: {
                subject: newTicket.problem,
              },
            };

            return {
              ticket: {
                total: data.tickets?.length ? data.total + 1 : 1,
                results: [tempTicket, ...data.tickets],
              },
            };
          }
        );

        return { previousTickets };
      },
      onError: (_, __, context) => {
        queryClient.setQueryData(
          [CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY, deviceId, 'TICKETS'],
          context?.previousTickets
        );
      },
    });
  };

  const useGetDealIdHubspot = () => {
    return useQuery({
      queryKey: [
        CRM_TICKETS_SCHEDULE_INSTALLATION_QUERY_KEY,
        'DEAL_ID_HUBSPOT',
        fleetId,
      ],
      queryFn: () => crmRequester.getHubpotDealId(fleetId),
      enabled: true,
      staleTime: Number.POSITIVE_INFINITY,
    });
  };

  return {
    getScheduleInstallationTicket,
    notResponsibleForInstallation,
    responsibleForInstallation,
    confirmInstallation,
    clientAlreadyHasATechnician,
    cancelInstallation,
    rateInstallation,
    useGetTicket,
    useCreateTicket,
    useGetDealIdHubspot,
    getDevolutionTickets,
  };
};

export default useCrm;
